import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { rolesService } from "../services/roles.service";
import type { CreateRoleInput, UpdateRoleInput } from "../types";

/** Backend `PaginationQuerySchema` maksimumi. */
export const ROLES_LIST_LIMIT = 100;

/**
 * Ro'yxat paneli uchun barcha rollar — bitta sahifa. Rollar soni kam
 * (3 tizim + bir nechta), shuning uchun qidiruv MIJOZ tomonida; `total`
 * limitdan oshsa panel buni ochiq aytadi (jimgina kesilmaydi).
 *
 * ⚠️ `usePaginatedQuery` EMAS — u sahifa holatini o'zida saqlaydi, panelga
 * esa sahifalash kerak emas.
 */
export function useAllRoles() {
	return useQuery({
		queryKey: ["roles", "all"],
		queryFn: () => rolesService.listAll(ROLES_LIST_LIMIT),
	});
}

/**
 * Bitta rol — tahrirlash paneli (`/roles/$roleId`). Ro'yxat orqali kelgan
 * foydalanuvchi uchun ma'lumot allaqachon keshda bo'ladi, lekin sahifa
 * to'g'ridan-to'g'ri URL orqali ham ochilishi mumkin (qayta yuklash,
 * xatcho'p) — shu sabab mustaqil so'rov kerak.
 */
export function useRole(id: string | undefined) {
	return useQuery({
		queryKey: ["roles", "detail", id],
		queryFn: () => rolesService.getById(id as string),
		enabled: Boolean(id),
	});
}

export function useResources() {
	return useQuery({
		queryKey: ["roles", "resources"],
		queryFn: () => rolesService.listResources(),
		// Resurslar backend'da kod bilan belgilangan — deploy orasida o'zgarmaydi.
		staleTime: Number.POSITIVE_INFINITY,
	});
}

function useRolesMutation<TVariables, TData>(
	mutationFn: (variables: TVariables) => Promise<TData>,
	successMessage: string
) {
	const { message } = App.useApp();
	const { t } = useTranslation("roles");
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["roles"] });
			// Rol o'zgarishi foydalanuvchilarning ko'rsatilgan rollariga ta'sir qiladi.
			queryClient.invalidateQueries({ queryKey: ["users"] });
			message.success(successMessage);
		},
		// Backend xabari aniq sababni aytadi (band ID, biriktirilgan rol,
		// tizim roli) — umumiy matn uni yutib yuborardi.
		onError: (error) => {
			message.error(getErrorMessage(error, t("messages.actionFailed")));
		},
	});
}

export function useCreateRole() {
	const { t } = useTranslation("roles");
	return useRolesMutation(
		(input: CreateRoleInput) => rolesService.create(input),
		t("messages.created")
	);
}

export function useUpdateRole() {
	const { t } = useTranslation("roles");
	return useRolesMutation(
		({ id, input }: { id: string; input: UpdateRoleInput }) => rolesService.update(id, input),
		t("messages.updated")
	);
}

export function useDeleteRole() {
	const { t } = useTranslation("roles");
	return useRolesMutation((id: string) => rolesService.remove(id), t("messages.deleted"));
}
